import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { api_url } from "../config/config";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const isToken = localStorage.getItem("authToken");

  /* ── Fetch cart count ── */
  const fetchCartCount = async () => {
    if (!isToken) return;
    try {
      const response = await axios.get(`${api_url}/count-cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setCartCount(response.data.count);
    } catch (error) {
      console.error("Error fetching cart count:", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, []);

  /* ── Fetch cart products ── */
  const fetchCartProducts = async () => {
    try {
      const response = await axios.get(`${api_url}/cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const data = response?.data?.data?.cartproduct || [];
      setCartItems(data);
    } catch (error) {
      console.error("Error fetching cart products:", error);
    }
  };

  useEffect(() => {
    fetchCartProducts();
  }, []);

  /* ── Add to Cart ── */
  const handleAddToCart = async (product, selectedVariantId = null) => {
    if (!isToken) {
      setShowLoginPopup(true);
      return;
    }

    const variantId = (
      selectedVariantId?.variant_id ||
      product.variant_id ||
      (Array.isArray(product.variant) && product.variant.length > 0
        ? product.variant[0]?.variant_id
        : null)
    )?.toString();

    if (!variantId || typeof variantId !== "string") {
      toast.error("Please select a valid variant before adding to cart.");
      return;
    }

    const payload = {
      product_id: product.product_id,
      variant_id: variantId,
      quantity: 1,
    };

    try {
      const response = await fetch(`${api_url}/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setShowDisable(false);
        toast.success("Product added to cart!", {
          position: "bottom-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setCartItems((prevCart) => {
          const existingIndex = prevCart.findIndex(
            (item) =>
              item.product_id === product.product_id &&
              item.variant_id === variantId,
          );
          if (existingIndex !== -1) {
            const updated = [...prevCart];
            updated[existingIndex].quantity += 1;
            return updated;
          }
          return [
            ...prevCart,
            { ...product, variant_id: variantId, quantity: 1 },
          ];
        });

        setCartCount((prev) => prev + 1);
      } else {
        setShowDisable(true);
        toast.error(data.message || "Failed to add product to cart.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to add product to cart:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  /* ── Remove from Cart ── */
  const handleRemoveFromCart = async (productId, variant_id) => {
    try {
      const response = await fetch(`${api_url}/cart`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ product_id: productId, variant_id }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Product removed from cart.", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
        });

        setCartItems((prev) =>
          prev.filter(
            (item) =>
              !(
                item.product_id === productId &&
                (item.variant_id === variant_id || !variant_id)
              ),
          ),
        );

        setCartCount((prev) => Math.max(0, prev - 1));
      } else {
        toast.error(data.message || "Failed to remove product.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to remove product from cart:", err);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  /* ── Logout ── */
  const handleLogout = () => {
    setCartItems([]);
    setCartCount(0);
    localStorage.removeItem("authToken");
    localStorage.removeItem("cart");
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        fetchCartProducts,
        setCartItems,
        handleAddToCart,
        handleRemoveFromCart,
        setCartCount,
        cartCount,
        fetchCartCount,
        handleLogout,
        showLoginPopup,
        setShowLoginPopup,
        showDisable,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
